<template>
  <div id="editor-wrapper">
    <div class="avatar" :class="{ hidden: !$root.userLoggedIn }">
      <div class="avatar-overlay" v-if="comment.length == 0"/>
      <avatar :user="user" size="sm" />
    </div>
    <div class="vue-easymde" id="vue-easymde">
      <p id="heading">
        {{heading}}
        <img :src="require('@img/icons-new-gray.svg')" v-if="!commentId" />
      </p>
      <div id="add-display-name" v-if="nameRequired">
        <div id="display-name-form">
          <input type="text" ref="fullName" data-testid="comment-display-name" placeholder="Full name" @keyup.enter.exact="submitDisplayName"/>
          <button id="submitDisplayName" href="#" @click="submitDisplayName()" data-testid="add-display-name">
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
        <span class="display-name-error" v-if="anonymousNameError">Name must be at least 3 characters long</span>
      </div>
      <div id="submit-comment" v-else>
        <vue-tribute :options="tributeOptions">
          <ResizeAuto>
            <template v-slot:default="{resize}">
              <textarea rows="1" id="new_comment" class="textarea vue-simplemde-textarea" :name="name" v-model="comment"
                @tribute-replaced="replacedName"
                :placeholder="isPersonal || !userLoggedIn ? 'Add to the conversation' : 'Type @ to mention a person'"
                @keydown.enter.exact.prevent
                @keyup.enter.exact="onEnter"
                @input="(e) => onInput(e, resize)"
                ref="new_comment"
              ></textarea>
            </template>
          </ResizeAuto>
        </vue-tribute>

        <button id="submitComment" name="commit" value="Post" @click="checkAnonymous()" data-disable-with="Post" data-testid="post-comment" class="editor-submit submitComment">
          <i class="fas fa-paper-plane" :class="{ 'active': comment.length > 0 }"></i>
        </button>
      </div>
      <div v-if="isTyping" class="collaborator-tip">
        Pro tip &#11088; &nbsp;:
        <a :href="collaborationLink">
          {{collaborationTip}}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import VueTribute from 'vue-tribute';
import EventBus from '../../event_bus';
import ResizeAuto from '../resize_auto';
import Avatar from "@cmp/shared/avatar";
import { mapState } from 'vuex';


import 'tributejs/tribute.css';


export default {
  components: { VueTribute, ResizeAuto, Avatar },
  props: {
    name: String,
    commentableType: String
  },
  data: function() {
    return {
      comment: "",
      heading: "Post new comment",
      commentId: null,
      mentions: [],
      disableEnter: false,
      displayName: localStorage.getItem('commentsDisplayName'),
      nameRequired: false,
      anonymousNameError: false,
      tributeOptions: {
        values: function(text, cb) { this.getUserNames(text, users => cb(users)); }.bind(this),
        lookup: "name",
        fillAttr: "name",

        noMatchTemplate: function () { return '<span style:"visibility: hidden;"></span>'; },
        menuItemLimit: 10
      },
      isTyping: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.user,
      isPersonal: state => state.currentOrganization.attributes.personal,
      commentableId: state => state.item.id,
    }),
    userLoggedIn: function(){
      return this.$root.userLoggedIn;
    },
    collaborationTip: function () {
      return this.isPersonal
        ? "Create a team to mention collabrators in comments!"
        : "Invite team members to mention them!";
    },
    collaborationLink: function() {
      return this.isPersonal
        ? "/purchase?period=annual&plan=team&trial=true&utm_source=viewer&utm_campaign=comments"
        : "#invite";
    },
  },
  mounted() {
    EventBus.$on("comment:submitted", this.receiveUpdate);
    EventBus.$on("comment:edit", this.editComment);
  },
  updated() {
    if(this.nameRequired){
      this.$refs.fullName.focus();
    }
  },
  methods: {
    newline: function(event) {
      const cur = $(event.target).val();
      $(event.target).val(cur + '\n');
    },
    onEnter: function(event) {
      if (this.disableEnter) {
        this.disableEnter = false;
        return;
      }

      this.checkAnonymous()
    },
    getUserNames: function(query, callback) {
      if(!this.userLoggedIn || query.length < 2) return callback([]);

      this.$store.dispatch('searchUsers', { params: { q: query } })
      .then(function (response) {
        let returnValue = [];
        for (const user of response["data"]["data"]["results"]["attributes"]["users"]) {
          returnValue.push(user);
        }
        callback(returnValue);
      });
    },
    replacedName: function(event) {
      this.disableEnter = true;
      this.mentions.push(event["detail"]["item"]["original"]);
    },
    emitEvent: function () {
      EventBus.$emit("comment:submitted");
    },
    editComment: function (comment, commentId) {
      this.heading = "Edit comment";
      this.comment = comment;
      this.commentId = commentId;
    },
    receiveUpdate: function() {
      this.heading = "Post new comment"
      this.comment = "";
      this.commentId = null;
    },
    submitDisplayName: function(){
      const name = this.$refs.fullName.value.trim();
      if(name.length > 3){
        this.anonymousNameError = false;
        this.displayName = name;
        localStorage.setItem('commentsDisplayName', name);
        this.checkAnonymous();
      }else {
        this.anonymousNameError = true;
      }
    },
    checkAnonymous: function(){
      if(this.comment.length === 0){ return; }

      if(!this.userLoggedIn && !this.displayName){
        this.nameRequired = true;
        this.heading = "Please add your name to comment";
      }else{
        this.nameRequired = false
        this.submitCommentEditor()
      }
    },
    submitCommentEditor: function (_) {
      localStorage.setItem("open_same_tab", true);
      let content = this.comment;

      const params = {
        "content": content.trim(),
        "commentable_id": this.commentableId,
        "commentable_type": this.commentableType,
        "mentions": this.mentions,
        "display_name": this.displayName
      }

      if(this.commentId) {
        this.$store.dispatch('updateComment', { commentId: this.commentId, params }).then(function(){ this.emitEvent(); }.bind(this));
      } else {
        this.$store.dispatch('createComment', params).then(function(){ this.emitEvent(); }.bind(this));
      }

      return false; // prevent normal submit
    },
    onInput: function(e, resizeHandler) {
      if (!this.isTyping) {
        this.isTyping = true;
      }

      if (resizeHandler) {
        resizeHandler(e);
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '../../../packs/application.scss';

  #editor-wrapper {
    display: flex;
  }

  .avatar {
    margin-top: .5rem;
    position: relative;

    &.hidden {
      visibility: hidden;
    }
  }

  .avatar-overlay {
    width: 3rem;
    height: 3rem;
    position: absolute;
    z-index: 1;
    background: rgba(231, 235, 240, 0.5);
    border-radius: 30px;
  }

  #heading {
    font-weight: bold;
    margin-bottom: 5px;
    color: $dark-gray;
  }

  #add-display-name {
    display: flex;
    flex-direction: column;

    #display-name-form {
      display: flex;
      justify-content: space-between;
      position: relative;

      input {
        flex-grow: 2;
        padding: .5rem;
        border-radius: 5px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        border: solid 1px #b0b0b0;
        background-color: var(--c-white);

        &:focus {
          outline: 0 none;
          border: 1px solid #b0b0b0;
        }
      }

      button {
        background: transparent;
        position: absolute;
        border: none;
        margin-right: 5px;
        top: 9px;
        right: 4px;

        .fa-paper-plane {
          color: $bright-blue;
        }
      }
    }

    .display-name-error {
      font-size: 0.7rem;
      color: $radical-red;
    }
  }

  .vue-easymde {
    flex-grow: 2;
    margin-left: 1rem;
    background-color: #ffffff;
    padding: 1rem;
    border-radius: 4px;

    .new-comment-text {
      font-family: SFPro;
      font-size: 0.75rem;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
    }

    .new-comment {
      margin-top: 0.75rem;
      display: flex;
    }

    .vue-simplemde-textarea {
      overflow: auto;
      resize: none;
      width: 100%;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
      border: solid 1px #b0b0b0;
      background-color: var(--c-white);
      padding: 0.5rem 2rem 0.5rem 0.5rem;
      max-height: 10rem;
      font-size: 0.75rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
    }

    .vue-simplemde-textarea:focus {
        outline: 0 none;
        -webkit-appearance: none;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        border: solid 1px #b0b0b0;
    }
  }

  #submit-comment {
    position: relative;
  }

  .editor-submit {
    border: none;
    position: absolute;
    position: absolute;
    top: 3px;
    right: 4px;
    background: transparent;

    .fa-paper-plane {
      color: $dark-gray;

      &.active {
        color: $bright-blue;
      }
    }
  }
  .editor-submit:focus {
    outline: none;
    box-shadow: none;
  }
  .collaborator-tip {
    color: $bright-blue;
  }
</style>
